import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import {
  FiTv, FiZap, FiShield, FiLayout, FiCheck,
  FiList, FiMonitor, FiDownload, FiAirplay, FiClock,
  FiGlobe, FiKey, FiX
} from 'react-icons/fi';

import MicrosoftStoreBtn from '../components/MicrosoftStoreBtn';
import { getProduct } from '../data/catalog';
import Reel from '../components/Reel';

const TVVERSE_WEB_URL = 'https://tvverse.mediaverselabs.com';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-card border border-border p-6 rounded-none hover:border-accent-red/50 transition-all group">
    <div className="w-12 h-12 rounded-md bg-accent-red/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
      <SafeIcon icon={icon} className="w-6 h-6 text-accent-red" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-muted text-sm leading-relaxed">{desc}</p>
  </div>
);

// Capability rows for the Windows / web comparison. CapNo is deliberately explicit:
// the web build has no Picture-in-Picture, casting or EPG, and saying so beats a
// visitor discovering it after signing up.
const CapYes = ({ children }) => (
  <li className="flex gap-3 items-start text-muted">
    <SafeIcon icon={FiCheck} className="w-4 h-4 mt-0.5 shrink-0 text-accent-red" />
    <span>{children}</span>
  </li>
);

const CapNo = ({ children }) => (
  <li className="flex gap-3 items-start text-muted/60">
    <SafeIcon icon={FiX} className="w-4 h-4 mt-0.5 shrink-0" />
    <span>{children}</span>
  </li>
);

const SpecItem = ({ title, value }) => (
  <div className="flex justify-between items-center py-3 border-b border-border">
    <span className="text-muted text-sm">{title}</span>
    <span className="text-white font-medium text-sm">{value}</span>
  </div>
);

const TVVerse = () => {
  const PRODUCT = getProduct('tvverse');

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-red/10 border border-accent-red/20 text-accent-red text-sm font-medium mb-6">
              <SafeIcon icon={FiTv} /> Re-imagining the Windows TV Experience
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
              Your World of TV,<br />
              <span className="text-accent-red">Organized & Ad-Free</span>
            </h1>
            <p className="text-xl text-muted max-w-3xl mx-auto mb-10 leading-relaxed">
              TVVerse is the most powerful and minimalist M3U player built for Windows,
              now also in the browser. Bring your own playlists and enjoy a premium
              interface without tracking or clutter.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
              <MicrosoftStoreBtn
                accentColor="bg-accent-red"
                className="px-10 py-4 scale-110"
                href={PRODUCT.storeLink}
              />
              <a
                href={TVVERSE_WEB_URL}
                className="flex items-center gap-3 px-8 py-4 rounded-md border border-border bg-card text-white font-semibold hover:border-accent-red/50 transition-colors"
              >
                <SafeIcon icon={FiGlobe} className="w-5 h-5 text-accent-red" />
                Open the web app
              </a>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-2 mt-8 text-sm text-muted">
              <span className="flex items-center gap-2">
                <SafeIcon icon={FiShield} className="text-accent-red" />
                Windows: {PRODUCT.priceNote}. 100% private.
              </span>
              <span className="flex items-center gap-2">
                <SafeIcon icon={FiKey} className="text-accent-red" />
                Web: invitation only.
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Reel — the frames for this product, so a visitor sees what it does. */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Reel — TVVerse</p>
          <Reel frames={PRODUCT.frames} label="TVVerse" sound={false} ratio="aspect-[16/10]" />
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Built for Power Users</h2>
            <p className="text-muted">A lightweight app that handles heavy-duty playlists with ease.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard 
              icon={FiZap} 
              title="Instant Loading" 
              desc="Optimized engine that parses thousands of channels in seconds without freezing." 
            />
            <FeatureCard 
              icon={FiMonitor} 
              title="Picture-in-Picture" 
              desc="Keep watching your favorite stream while working on other tasks with Mini-Player." 
            />
            <FeatureCard 
              icon={FiLayout} 
              title="EPG Support" 
              desc="Full Electronic Program Guide integration to see what's playing now and next." 
            />
            <FeatureCard 
              icon={FiAirplay} 
              title="Cast to Device" 
              desc="Seamlessly stream content from your PC to compatible DLNA or Chromecast devices." 
            />
          </div>
        </div>
      </section>

      {/* Windows app vs web */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Two ways to watch</h2>
            <p className="text-muted">
              The Windows app is the full experience. The web app goes wherever you are.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-card border border-accent-red/40 rounded-none p-8">
              <div className="flex items-center gap-3 mb-2">
                <SafeIcon icon={FiMonitor} className="w-6 h-6 text-accent-red" />
                <h3 className="text-xl font-bold text-white">Windows app</h3>
              </div>
              <p className="text-muted text-sm mb-6">{PRODUCT.priceNote}, lifetime license.</p>
              <ul className="space-y-3 text-sm">
                <CapYes>Everything in the web app</CapYes>
                <CapYes>Picture-in-Picture mini player</CapYes>
                <CapYes>EPG guide (XMLTV)</CapYes>
                <CapYes>Cast to DLNA and Chromecast</CapYes>
                <CapYes>Load playlists from a local file</CapYes>
              </ul>
              <a
                href={PRODUCT.storeLink}
                className="mt-8 w-full flex items-center justify-center gap-2 py-3 rounded-md bg-white text-background font-bold hover:bg-gray-200 transition-colors"
              >
                <SafeIcon icon={FiDownload} /> Get it on Windows
              </a>
            </div>

            <div className="bg-card border border-border rounded-none p-8">
              <div className="flex items-center gap-3 mb-2">
                <SafeIcon icon={FiGlobe} className="w-6 h-6 text-accent-red" />
                <h3 className="text-xl font-bold text-white">Web app</h3>
              </div>
              <p className="text-muted text-sm mb-6">Invitation only. Nothing to install.</p>
              <ul className="space-y-3 text-sm">
                <CapYes>Any device with a browser</CapYes>
                <CapYes>Filter by country, language, category and quality</CapYes>
                <CapYes>Favorites and recently watched</CapYes>
                <CapNo>No Picture-in-Picture</CapNo>
                <CapNo>No casting, no EPG yet</CapNo>
              </ul>
              <a
                href={TVVERSE_WEB_URL}
                className="mt-8 w-full flex items-center justify-center gap-2 py-3 rounded-md border border-border text-white font-bold hover:border-accent-red/50 transition-colors"
              >
                <SafeIcon icon={FiGlobe} /> Open the web app
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-20 items-center">
            <div className="flex-1 space-y-8">
              <h2 className="text-4xl font-bold text-white leading-tight">
                Privacy is not an option.<br />
                <span className="text-accent-red">It's the foundation.</span>
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-accent-red/10 flex items-center justify-center text-accent-red">
                    <SafeIcon icon={FiCheck} />
                  </div>
                  <p className="text-muted"><strong className="text-white">Zero Telemetry:</strong> We don't track what you watch, when you watch, or where you're from.</p>
                </div>
                <div className="flex gap-4">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-accent-red/10 flex items-center justify-center text-accent-red">
                    <SafeIcon icon={FiCheck} />
                  </div>
                  <p className="text-muted"><strong className="text-white">Local Storage:</strong> Your playlists and favorites stay on your machine. Never on our servers.</p>
                </div>
                <div className="flex gap-4">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-accent-red/10 flex items-center justify-center text-accent-red">
                    <SafeIcon icon={FiCheck} />
                  </div>
                  <p className="text-muted"><strong className="text-white">Ad-Free UI:</strong> No banners, no popups, no distractions. Just your content.</p>
                </div>
              </div>
            </div>
            
            <div className="flex-1 w-full max-w-md">
              <div className="bg-card border border-border rounded-none p-8 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <SafeIcon icon={FiList} className="text-accent-red" /> Tech Specs
                </h3>
                <div className="space-y-1">
                  <SpecItem title="Platform" value="Windows 10/11 + web" />
                  <SpecItem title="File Support" value="M3U, M3U8, TXT" />
                  <SpecItem title="EPG Format" value="XMLTV" />
                  <SpecItem title="Architecture" value="x64 / ARM64" />
                  <SpecItem title="App Size" value="~45 MB" />
                  <SpecItem title="Version" value="2.9.0 (Latest)" />
                </div>
                <div className="mt-8 pt-6 border-t border-border">
                   <div className="flex items-center justify-between mb-4">
                     <span className="text-white font-bold">Lifetime License</span>
                     <span className="text-accent-red font-bold text-xl">{PRODUCT.price}</span>
                   </div>
                   <p className="text-xs text-muted mb-6">One-time payment for lifetime access on your Windows devices. No subscriptions, no hidden fees.</p>
                   <a 
                    href={PRODUCT.storeLink}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-md bg-white text-background font-bold hover:bg-gray-200 transition-colors"
                   >
                     <SafeIcon icon={FiDownload} /> Get it on Windows
                   </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-accent-red/5 -z-10" />
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-white mb-6">Experience TV as it should be.</h2>
          <p className="text-muted mb-10">
            Join thousands of users who have switched to a faster, cleaner, and more private way to watch their favorite channels on Windows for just {PRODUCT.price}.
          </p>
          <MicrosoftStoreBtn 
            accentColor="bg-accent-red" 
            className="mx-auto px-12 py-5 scale-110" 
            href={PRODUCT.storeLink}
          />
        </div>
      </section>
    </div>
  );
};

export default TVVerse;